import { useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  useContentToken,
  useEditMode,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

type TabId = 'events' | 'groups' | 'chat' | 'market';

const tourCtaDef: SlotGroupDefinition = {
  label: 'Schedule a Tour CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/schedule-a-tour',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Schedule a Tour',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
    vibesite_event: {
      label: 'Tracked Event',
      type: 'event',
      defaultValue: 'schedule_tour_clicked',
    },
  },
};

const cobuLinkDef: SlotGroupDefinition = {
  label: 'Cobu Branding Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue:
        'https://communities.livecobu.com/state/tx/city/san-antonio/property/1221-broadway-lofts/',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Cobu',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const event1Def: SlotGroupDefinition = {
  label: 'Event 1',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://cobu-production-backend.s3.amazonaws.com/groupevents/0df0482b-d8a8-4289-a1f3-c5b3759d8afb/a4b5b135-4b1b-4050-9750-e24c6cd08015/e6910b98-0cbe-4bc6-a301-e3c66719612c.png',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: "Lily's Bloom Bar - Build your own bouquet!",
    },
    date: {
      label: 'Date',
      type: 'text',
      defaultValue: 'Saturday · Jul 18 · 11:00 AM',
    },
    attendees: {
      label: 'Attendees',
      type: 'text',
      defaultValue: '32 people attending',
    },
    description: {
      label: 'Description',
      type: 'text',
      defaultValue:
        "Join us for a fun and creative experience where you'll build your own bouquet. Please express interest — it helps us plan better!",
    },
  },
};

const event2Def: SlotGroupDefinition = {
  label: 'Event 2',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://cobu-production-backend.s3.amazonaws.com/groupevents/0df0482b-d8a8-4289-a1f3-c5b3759d8afb/45d76fd2-8607-4ccc-bca8-741b008102a9/34431ada-b6ad-49b6-ad3d-51bc8aa5c287.png',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: 'Happy Hour @ NOLA',
    },
    date: {
      label: 'Date',
      type: 'text',
      defaultValue: 'Monday · Jun 29 · 5:30 PM',
    },
    attendees: {
      label: 'Attendees',
      type: 'text',
      defaultValue: '45 people attending',
    },
    description: {
      label: 'Description',
      type: 'text',
      defaultValue:
        'Join us for Happy Hour at NOLA, 1101 Broadway St — drinks and beignets while supplies last.',
    },
  },
};

const group1Def: SlotGroupDefinition = {
  label: 'Group 1',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://cobu-production-backend.s3.amazonaws.com/groupImages/2f378e37-da46-491d-b5a3-16eaa9acd761/cd108147-c954-4845-8239-98a9302c7257.jpeg',
    },
    title: { label: 'Title', type: 'text', defaultValue: 'Foodie Group' },
    members: { label: 'Members', type: 'text', defaultValue: '49 members' },
    description: {
      label: 'Description',
      type: 'text',
      defaultValue:
        "We're all about food and friends — potlucks, taco trucks, take-out nights, and cooking adventures.",
    },
    footer: {
      label: 'Footer',
      type: 'text',
      defaultValue: 'Open To All Residents',
    },
  },
};

const group2Def: SlotGroupDefinition = {
  label: 'Group 2',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://cobu-production-backend.s3.amazonaws.com/groupImages/41cbb5f3-5206-47e9-87b3-164f6cfc445c/360a2673-2704-45b7-8a89-0e7b6fcdc49c.jpeg',
    },
    title: { label: 'Title', type: 'text', defaultValue: 'Tasting Club' },
    members: { label: 'Members', type: 'text', defaultValue: '23 members' },
    description: {
      label: 'Description',
      type: 'text',
      defaultValue:
        'Explore wine, beer, whiskey, and cocktails with neighbors around Downtown San Antonio.',
    },
    footer: {
      label: 'Footer',
      type: 'text',
      defaultValue: 'Open To All Residents',
    },
  },
};

const group3Def: SlotGroupDefinition = {
  label: 'Group 3',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://cobu-production-backend.s3.amazonaws.com/groupImages/8c9a5869-efe4-4ce6-8bb7-56ee962cc166/3974c179-70d6-4827-998c-fd8a4cd88dd1.jpeg',
    },
    title: { label: 'Title', type: 'text', defaultValue: 'Dog Group' },
    members: { label: 'Members', type: 'text', defaultValue: '21 members' },
    description: {
      label: 'Description',
      type: 'text',
      defaultValue:
        'Walks, dog-park hangouts, and puppy play dates for neighbors and their pups.',
    },
    footer: {
      label: 'Footer',
      type: 'text',
      defaultValue: 'Open To All Residents',
    },
  },
};

const group4Def: SlotGroupDefinition = {
  label: 'Group 4',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://cobu-production-backend.s3.amazonaws.com/groupImages/f80b4a4c-746f-4a9d-9748-b94a120a6096/e00535cf-a136-4966-9731-6033e0d983a5.png',
    },
    title: { label: 'Title', type: 'text', defaultValue: 'Yoga Group' },
    members: { label: 'Members', type: 'text', defaultValue: '15 members' },
    description: {
      label: 'Description',
      type: 'text',
      defaultValue:
        'A group that comes together to practice yoga and mindfulness in community.',
    },
    footer: {
      label: 'Footer',
      type: 'text',
      defaultValue: 'Open To All Residents',
    },
  },
};

const chat1Def: SlotGroupDefinition = {
  label: 'Chat Thread 1',
  fields: {
    author: { label: 'Author', type: 'text', defaultValue: 'Resident' },
    time: { label: 'Time', type: 'text', defaultValue: '6 days ago' },
    text: {
      label: 'Message',
      type: 'text',
      defaultValue:
        'Mehmuna and I wanted to give a HUGE thank you to the 1221 management team for hosting the trip to the San Antonio Zoo!',
    },
    reply_author: { label: 'Reply Author', type: 'text', defaultValue: 'Resident' },
    reply_time: { label: 'Reply Time', type: 'text', defaultValue: '5 days ago' },
    reply_text: {
      label: 'Reply',
      type: 'text',
      defaultValue: 'It was so much fun — can\'t wait for the next outing!',
    },
    reactions: {
      label: 'Reactions Label',
      type: 'text',
      defaultValue: '1 reaction',
    },
  },
};

const chat2Def: SlotGroupDefinition = {
  label: 'Chat Thread 2',
  fields: {
    author: { label: 'Author', type: 'text', defaultValue: 'Resident' },
    time: { label: 'Time', type: 'text', defaultValue: '2 weeks ago' },
    text: {
      label: 'Message',
      type: 'text',
      defaultValue:
        'Just a reminder — trash cans can go out at 6pm and need to be brought in by 9am. Have a great day!',
    },
    reply_author: { label: 'Reply Author', type: 'text', defaultValue: '' },
    reply_time: { label: 'Reply Time', type: 'text', defaultValue: '' },
    reply_text: { label: 'Reply', type: 'text', defaultValue: '' },
    reactions: { label: 'Reactions Label', type: 'text', defaultValue: '' },
  },
};

const market1Def: SlotGroupDefinition = {
  label: 'Marketplace Post 1',
  fields: {
    author: { label: 'Author', type: 'text', defaultValue: 'Resident' },
    time: { label: 'Time', type: 'text', defaultValue: '3 days ago' },
    text: {
      label: 'Post Text',
      type: 'text',
      defaultValue:
        'Unopened dehumidifier for $20 — bought two thinking one wouldn’t be enough. Great for rainy days and condensation on windows.',
    },
    image1: {
      label: 'Image 1',
      type: 'image',
      defaultValue:
        'https://cobu-production-backend.s3.amazonaws.com/comment_images/b613b6ca-ba20-4839-a482-8158e73d92c6/cc169ca8-defa-4f63-8a4b-506ef319041b.jpeg',
    },
    image2: {
      label: 'Image 2',
      type: 'image',
      defaultValue:
        'https://cobu-production-backend.s3.amazonaws.com/comment_images/b613b6ca-ba20-4839-a482-8158e73d92c6/32313a49-474b-4861-91f9-7b65b1485cb3.jpeg',
    },
    image3: {
      label: 'Image 3',
      type: 'image',
      defaultValue:
        'https://cobu-production-backend.s3.amazonaws.com/comment_images/b613b6ca-ba20-4839-a482-8158e73d92c6/5c7207a9-fa29-41ae-8385-566c3832202c.jpeg',
    },
  },
};

const market2Def: SlotGroupDefinition = {
  label: 'Marketplace Post 2',
  fields: {
    author: { label: 'Author', type: 'text', defaultValue: 'Resident' },
    time: { label: 'Time', type: 'text', defaultValue: '1 week ago' },
    text: {
      label: 'Post Text',
      type: 'text',
      defaultValue:
        'Margarita blender, small kitchen appliances, and more — message me if interested!',
    },
    image1: {
      label: 'Image 1',
      type: 'image',
      defaultValue:
        'https://cobu-production-backend.s3.amazonaws.com/comment_images/822d875c-5e85-4aec-91fa-e82dc2c9da0f/eba6426d-6bd3-4f0c-9e7a-ee72c6161e81.jpeg',
    },
    image2: {
      label: 'Image 2',
      type: 'image',
      defaultValue:
        'https://cobu-production-backend.s3.amazonaws.com/comment_images/822d875c-5e85-4aec-91fa-e82dc2c9da0f/41201aa8-5cc7-4760-835c-7e2631bc6d5a.jpeg',
    },
    image3: {
      label: 'Image 3',
      type: 'image',
      defaultValue:
        'https://cobu-production-backend.s3.amazonaws.com/comment_images/822d875c-5e85-4aec-91fa-e82dc2c9da0f/daa98a7e-daf0-4c38-a762-a665df52f3c7.jpeg',
    },
  },
};

const market3Def: SlotGroupDefinition = {
  label: 'Marketplace Post 3',
  fields: {
    author: { label: 'Author', type: 'text', defaultValue: 'Resident' },
    time: { label: 'Time', type: 'text', defaultValue: '2 weeks ago' },
    text: {
      label: 'Post Text',
      type: 'text',
      defaultValue: 'Nintendo Switch + games — gently used, works great. DM for details.',
    },
    image1: {
      label: 'Image 1',
      type: 'image',
      defaultValue:
        'https://cobu-production-backend.s3.amazonaws.com/comment_images/73a3e306-a5a6-474a-b334-806497d93bbc/625e2e8a-3265-4888-8ad1-ea9451541d0f.jpeg',
    },
    image2: {
      label: 'Image 2',
      type: 'image',
      defaultValue:
        'https://cobu-production-backend.s3.amazonaws.com/comment_images/73a3e306-a5a6-474a-b334-806497d93bbc/9abf515a-76f0-4dbb-a562-f2be6ab91690.jpeg',
    },
    image3: { label: 'Image 3', type: 'image', defaultValue: '' },
  },
};

function hasText(val: unknown): boolean {
  if (val == null) return false;
  const s = String(val).trim().toLowerCase();
  return s !== '' && s !== 'false' && s !== 'null' && s !== 'undefined';
}

const TABS: { id: TabId; label: string }[] = [
  { id: 'events', label: 'Events' },
  { id: 'groups', label: 'Groups' },
  { id: 'chat', label: 'Chat' },
  { id: 'market', label: 'Market' },
];

function EventCardBody({
  event,
}: {
  event: {
    image?: string | null;
    title?: string | null;
    date?: string | null;
    attendees?: string | null;
    description?: string | null;
  };
}) {
  return (
    <article className="cobu-media-card">
      <div className="cobu-media-frame">
        {hasText(event.image) ? (
          <VibesitesImage
            url={event.image || ''}
            alt={event.title || 'Community event'}
            className="cobu-media-img"
            sizes="(max-width: 768px) 100vw, 50vw"
            fill
          />
        ) : (
          <div className="cobu-media-fallback" />
        )}
        {hasText(event.attendees) ? <span className="cobu-badge">{event.attendees}</span> : null}
      </div>
      <div className="cobu-media-body">
        <h3 className="cobu-card-title">{event.title}</h3>
        {hasText(event.date) ? <p className="cobu-meta">{event.date}</p> : null}
        {hasText(event.description) ? <p className="cobu-desc">{event.description}</p> : null}
      </div>
    </article>
  );
}

function GroupCardBody({
  group,
}: {
  group: {
    image?: string | null;
    title?: string | null;
    members?: string | null;
    description?: string | null;
    footer?: string | null;
  };
}) {
  return (
    <article className="cobu-media-card">
      <div className="cobu-media-frame">
        {hasText(group.image) ? (
          <VibesitesImage
            url={group.image || ''}
            alt={group.title || 'Interest group'}
            className="cobu-media-img"
            sizes="(max-width: 768px) 100vw, 50vw"
            fill
          />
        ) : (
          <div className="cobu-media-fallback" />
        )}
      </div>
      <div className="cobu-media-body">
        <div className="cobu-group-heading">
          <h3 className="cobu-card-title">{group.title}</h3>
          {hasText(group.members) ? <span className="cobu-badge">{group.members}</span> : null}
        </div>
        {hasText(group.description) ? <p className="cobu-desc">{group.description}</p> : null}
        {hasText(group.footer) ? <p className="cobu-group-footer">{group.footer}</p> : null}
      </div>
    </article>
  );
}

function ChatThreadBody({
  chat,
}: {
  chat: {
    author?: string | null;
    time?: string | null;
    text?: string | null;
    reply_author?: string | null;
    reply_time?: string | null;
    reply_text?: string | null;
    reactions?: string | null;
  };
}) {
  return (
    <article className="cobu-thread">
      <div className="cobu-msg">
        <span className="cobu-avatar" aria-hidden>
          R
        </span>
        <div className="cobu-msg-main">
          <div className="cobu-msg-head">
            <span className="cobu-msg-author">{chat.author || 'Resident'}</span>
            {hasText(chat.time) ? <span className="cobu-msg-time">{chat.time}</span> : null}
          </div>
          <p className="cobu-msg-text">{chat.text}</p>
          {hasText(chat.reactions) ? (
            <p className="cobu-reactions">
              <span aria-hidden>❤️ 😂 👍</span> {chat.reactions}
            </p>
          ) : null}
        </div>
      </div>
      {hasText(chat.reply_text) ? (
        <div className="cobu-msg cobu-msg-reply">
          <span className="cobu-avatar" aria-hidden>
            R
          </span>
          <div className="cobu-msg-main">
            <div className="cobu-msg-head">
              <span className="cobu-msg-author">{chat.reply_author || 'Resident'}</span>
              {hasText(chat.reply_time) ? (
                <span className="cobu-msg-time">{chat.reply_time}</span>
              ) : null}
            </div>
            <p className="cobu-msg-text">{chat.reply_text}</p>
          </div>
        </div>
      ) : null}
    </article>
  );
}

function MarketPostBody({
  post,
}: {
  post: {
    author?: string | null;
    time?: string | null;
    text?: string | null;
    image1?: string | null;
    image2?: string | null;
    image3?: string | null;
  };
}) {
  const images = [post.image1, post.image2, post.image3].filter(hasText) as string[];
  return (
    <article className="cobu-market-post">
      <div className="cobu-msg-head cobu-market-head">
        <span className="cobu-avatar" aria-hidden>
          R
        </span>
        <span className="cobu-msg-author">{post.author || 'Resident'}</span>
        {hasText(post.time) ? <span className="cobu-msg-time">{post.time}</span> : null}
      </div>
      {hasText(post.text) ? <p className="cobu-msg-text">{post.text}</p> : null}
      {images.length > 0 ? (
        <div className="cobu-market-thumbs">
          {images.map((url) => (
            <div key={url} className="cobu-market-thumb-frame">
              <VibesitesImage url={url} alt="" className="cobu-market-thumb" sizes="88px" fill />
            </div>
          ))}
        </div>
      ) : null}
    </article>
  );
}

export function CobuEmbed() {
  const [tab, setTab] = useState<TabId>('events');
  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;

  const tourCta = useSlotGroup('cobuembed.tour_cta', tourCtaDef);
  const cobuLink = useSlotGroup('cobuembed.cobu_link', cobuLinkDef);

  const event1 = useSlotGroup('cobuembed.event1', event1Def);
  const event2 = useSlotGroup('cobuembed.event2', event2Def);
  const group1 = useSlotGroup('cobuembed.group1', group1Def);
  const group2 = useSlotGroup('cobuembed.group2', group2Def);
  const group3 = useSlotGroup('cobuembed.group3', group3Def);
  const group4 = useSlotGroup('cobuembed.group4', group4Def);
  const chat1 = useSlotGroup('cobuembed.chat1', chat1Def);
  const chat2 = useSlotGroup('cobuembed.chat2', chat2Def);
  const market1 = useSlotGroup('cobuembed.market1', market1Def);
  const market2 = useSlotGroup('cobuembed.market2', market2Def);
  const market3 = useSlotGroup('cobuembed.market3', market3Def);

  const poweredBy = useContentToken('cobuembed.powered_by');
  const eventsHeading = useContentToken('cobuembed.events_heading');
  const groupsHeading = useContentToken('cobuembed.groups_heading');
  const chatHeading = useContentToken('cobuembed.chat_heading');
  const marketHeading = useContentToken('cobuembed.market_heading');

  return (
    <section className="cobu-embed bg-background text-foreground pb-16 md:pb-24">
      <div className="cobu-inner max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={tab === 'events' ? undefined : 'cobu-heading-inactive'} aria-hidden={tab !== 'events'}>
          <EditableToken path="cobuembed.events_heading" as="h2" className="cobu-intro font-body text-center">
            {eventsHeading || 'Events happening at 1221 Broadway Lofts'}
          </EditableToken>
        </div>
        <div className={tab === 'groups' ? undefined : 'cobu-heading-inactive'} aria-hidden={tab !== 'groups'}>
          <EditableToken path="cobuembed.groups_heading" as="h2" className="cobu-intro font-body text-center">
            {groupsHeading || 'Groups to join and create'}
          </EditableToken>
        </div>
        <div className={tab === 'chat' ? undefined : 'cobu-heading-inactive'} aria-hidden={tab !== 'chat'}>
          <EditableToken path="cobuembed.chat_heading" as="h2" className="cobu-intro font-body text-center">
            {chatHeading || 'Chat with your neighbors'}
          </EditableToken>
        </div>
        <div className={tab === 'market' ? undefined : 'cobu-heading-inactive'} aria-hidden={tab !== 'market'}>
          <EditableToken path="cobuembed.market_heading" as="h2" className="cobu-intro font-body text-center">
            {marketHeading || 'Buy, sell, and share with neighbors'}
          </EditableToken>
        </div>

        <div className="flex justify-center mb-8 md:mb-10">
          <EditableSlotGroup groupPath="cobuembed.tour_cta" definition={tourCtaDef}>
            <a
              href={tourCta.url || '/schedule-a-tour'}
              target={tourCta._target || '_self'}
              className="cobu-tour-cta font-heading text-sm uppercase tracking-wider text-secondary-foreground"
              data-vibesite-event={tourCta.vibesite_event || 'schedule_tour_clicked'}
            >
              {tourCta.label || 'Schedule a Tour'}
            </a>
          </EditableSlotGroup>
        </div>

        <div className="cobu-panel">
          <div className="cobu-tabs" role="tablist" aria-label="Community sections">
            {TABS.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={tab === item.id}
                className={`cobu-tab font-heading text-sm uppercase tracking-wider${
                  tab === item.id ? ' is-active' : ''
                }`}
                onClick={() => setTab(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="cobu-panel-body" role="tabpanel">
            {tab === 'events' && (
              <div className="cobu-grid">
                {(isEditMode || hasText(event1.title)) && (
                  <EditableSlotGroup groupPath="cobuembed.event1" definition={event1Def}>
                    <EventCardBody event={event1} />
                  </EditableSlotGroup>
                )}
                {(isEditMode || hasText(event2.title)) && (
                  <EditableSlotGroup groupPath="cobuembed.event2" definition={event2Def}>
                    <EventCardBody event={event2} />
                  </EditableSlotGroup>
                )}
              </div>
            )}

            {tab === 'groups' && (
              <div className="cobu-grid">
                {(isEditMode || hasText(group1.title)) && (
                  <EditableSlotGroup groupPath="cobuembed.group1" definition={group1Def}>
                    <GroupCardBody group={group1} />
                  </EditableSlotGroup>
                )}
                {(isEditMode || hasText(group2.title)) && (
                  <EditableSlotGroup groupPath="cobuembed.group2" definition={group2Def}>
                    <GroupCardBody group={group2} />
                  </EditableSlotGroup>
                )}
                {(isEditMode || hasText(group3.title)) && (
                  <EditableSlotGroup groupPath="cobuembed.group3" definition={group3Def}>
                    <GroupCardBody group={group3} />
                  </EditableSlotGroup>
                )}
                {(isEditMode || hasText(group4.title)) && (
                  <EditableSlotGroup groupPath="cobuembed.group4" definition={group4Def}>
                    <GroupCardBody group={group4} />
                  </EditableSlotGroup>
                )}
              </div>
            )}

            {tab === 'chat' && (
              <div className="cobu-stack">
                {(isEditMode || hasText(chat1.text)) && (
                  <EditableSlotGroup groupPath="cobuembed.chat1" definition={chat1Def}>
                    <ChatThreadBody chat={chat1} />
                  </EditableSlotGroup>
                )}
                {(isEditMode || hasText(chat2.text)) && (
                  <EditableSlotGroup groupPath="cobuembed.chat2" definition={chat2Def}>
                    <ChatThreadBody chat={chat2} />
                  </EditableSlotGroup>
                )}
              </div>
            )}

            {tab === 'market' && (
              <div className="cobu-stack">
                {(isEditMode || hasText(market1.text)) && (
                  <EditableSlotGroup groupPath="cobuembed.market1" definition={market1Def}>
                    <MarketPostBody post={market1} />
                  </EditableSlotGroup>
                )}
                {(isEditMode || hasText(market2.text)) && (
                  <EditableSlotGroup groupPath="cobuembed.market2" definition={market2Def}>
                    <MarketPostBody post={market2} />
                  </EditableSlotGroup>
                )}
                {(isEditMode || hasText(market3.text)) && (
                  <EditableSlotGroup groupPath="cobuembed.market3" definition={market3Def}>
                    <MarketPostBody post={market3} />
                  </EditableSlotGroup>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="cobu-powered">
          <EditableToken path="cobuembed.powered_by" as="p" className="cobu-powered-label">
            {poweredBy || 'Community Powered by'}
          </EditableToken>
          <EditableSlotGroup groupPath="cobuembed.cobu_link" definition={cobuLinkDef}>
            <a
              href={
                cobuLink.url ||
                'https://communities.livecobu.com/state/tx/city/san-antonio/property/1221-broadway-lofts/'
              }
              target={cobuLink._target || '_blank'}
              rel="noopener noreferrer"
              className="cobu-logo-link font-heading"
            >
              {cobuLink.label || 'Cobu'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .cobu-inner {
          position: relative;
        }
        .cobu-heading-inactive {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }
        .cobu-intro {
          margin: 0 0 1.15rem;
          color: var(--vibe-color-text-muted, #6b6b6b);
          font-size: 1.15rem;
          font-weight: 500;
          line-height: 1.4;
        }
        .cobu-tour-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.7rem 2.25rem;
          border-radius: 9999px;
          background: var(--vibe-color-secondary, #b4af2d);
          text-decoration: none;
          font-weight: 600;
          letter-spacing: 0.06em;
          transition: filter 0.2s ease, transform 0.2s ease;
        }
        .cobu-tour-cta:hover {
          filter: brightness(0.95);
          transform: translateY(-1px);
        }
        .cobu-panel {
          border: 1px solid #d8d8d8;
          background: #fff;
          overflow: hidden;
        }
        .cobu-tabs {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          border-bottom: 1px solid #d8d8d8;
          background: #fff;
        }
        .cobu-tab {
          appearance: none;
          background: #fff;
          border: 0;
          border-right: 1px solid #d8d8d8;
          border-bottom: 4px solid transparent;
          margin-bottom: -1px;
          padding: 1rem 0.5rem;
          color: #5c5c5c;
          cursor: pointer;
          font-weight: 600;
          letter-spacing: 0.08em;
          transition: color 0.15s ease, background 0.15s ease, border-color 0.15s ease;
        }
        .cobu-tab:last-child {
          border-right: 0;
        }
        .cobu-tab:hover:not(.is-active) {
          color: #1a1a1a;
          background: #fafafa;
        }
        .cobu-tab.is-active {
          background: var(--vibe-color-secondary, #b4af2d);
          color: var(--vibe-color-text-on-secondary, #fff);
          border-bottom-color: color-mix(in srgb, var(--vibe-color-secondary, #b4af2d) 65%, #000);
        }
        .cobu-panel-body {
          padding: 1.25rem;
          background: #fff;
        }
        .cobu-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 1.25rem;
        }
        .cobu-stack {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .cobu-media-card {
          border: 1px solid #d8d8d8;
          background: #fff;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .cobu-media-frame {
          position: relative;
          aspect-ratio: 3 / 2;
          background: #f3f3f3;
          overflow: hidden;
        }
        .cobu-media-img,
        .cobu-media-fallback {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .cobu-media-body {
          padding: 0.95rem 1rem 1.1rem;
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          flex: 1;
        }
        .cobu-badge {
          display: inline-flex;
          align-items: center;
          padding: 0.2rem 0.55rem;
          border-radius: 4px;
          background: #f5e66e;
          color: #1a1a1a;
          font-family: var(--vibe-font-body);
          font-size: 0.72rem;
          font-weight: 600;
          line-height: 1.2;
          white-space: nowrap;
        }
        .cobu-media-frame .cobu-badge {
          position: absolute;
          left: 0.65rem;
          bottom: 0.65rem;
        }
        .cobu-group-heading {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 0.75rem;
        }
        .cobu-group-footer {
          margin: auto 0 0;
          padding-top: 0.75rem;
          text-align: center;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          color: #6b6b6b;
        }
        .cobu-card-title {
          font-family: var(--vibe-font-heading);
          font-size: 1.05rem;
          font-weight: 700;
          line-height: 1.3;
          margin: 0;
          color: #1a1a1a;
        }
        .cobu-meta {
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          color: #6b6b6b;
          margin: 0;
        }
        .cobu-desc {
          font-family: var(--vibe-font-body);
          font-size: 0.92rem;
          line-height: 1.5;
          margin: 0.25rem 0 0;
          color: #444;
        }
        .cobu-thread,
        .cobu-market-post {
          border: 1px solid #d8d8d8;
          border-radius: 6px;
          background: #fff;
          padding: 1rem 1.1rem;
        }
        .cobu-msg {
          display: flex;
          gap: 0.75rem;
          align-items: flex-start;
        }
        .cobu-msg-reply {
          margin-top: 0.85rem;
          margin-left: 2.25rem;
          padding-top: 0.85rem;
          border-top: 1px solid #eee;
        }
        .cobu-avatar {
          flex-shrink: 0;
          width: 36px;
          height: 36px;
          border-radius: 9999px;
          background: #f5e66e;
          color: #6b6b6b;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-family: var(--vibe-font-heading);
          font-size: 0.95rem;
          font-weight: 700;
        }
        .cobu-msg-main {
          min-width: 0;
          flex: 1;
        }
        .cobu-msg-head,
        .cobu-market-head {
          display: flex;
          align-items: center;
          gap: 0.55rem;
          margin-bottom: 0.35rem;
        }
        .cobu-market-head {
          margin-bottom: 0.65rem;
        }
        .cobu-msg-author {
          font-family: var(--vibe-font-body);
          font-size: 0.92rem;
          font-weight: 700;
          color: #1a1a1a;
        }
        .cobu-msg-time {
          margin-left: auto;
          font-family: var(--vibe-font-body);
          font-size: 0.78rem;
          color: #8a8a8a;
        }
        .cobu-msg-text {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.5;
          color: #333;
          margin: 0;
        }
        .cobu-reactions {
          margin: 0.55rem 0 0;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          color: #6b6b6b;
        }
        .cobu-market-thumbs {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-top: 0.75rem;
        }
        .cobu-market-thumb-frame {
          position: relative;
          width: 88px;
          height: 88px;
          overflow: hidden;
          border-radius: 4px;
          border: 1px solid #e5e5e5;
          flex-shrink: 0;
          background: #f3f3f3;
        }
        .cobu-market-thumb {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .cobu-powered {
          margin-top: 2rem;
          text-align: center;
        }
        .cobu-powered-label {
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          color: var(--vibe-color-text-muted);
          margin: 0 0 0.35rem;
        }
        .cobu-logo-link {
          display: inline-block;
          font-size: clamp(2rem, 4vw, 2.75rem);
          font-weight: 700;
          letter-spacing: 0.02em;
          color: color-mix(in srgb, var(--vibe-color-text) 45%, transparent);
          text-decoration: none;
        }
        .cobu-logo-link:hover {
          color: var(--vibe-color-text);
        }
        @media (max-width: 767px) {
          .cobu-grid {
            grid-template-columns: 1fr;
          }
          .cobu-tab {
            font-size: 0.65rem;
            letter-spacing: 0.06em;
            padding: 0.85rem 0.2rem;
          }
          .cobu-tour-cta {
            padding: 0.65rem 1.75rem;
            font-size: 0.8rem;
          }
          .cobu-msg-reply {
            margin-left: 1rem;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CobuEmbed',
  displayName: 'Cobu Community',
  tokens: {
    'cobuembed.events_heading': {
      label: 'Events Tab Heading',
      type: 'text',
      defaultValue: 'Events happening at 1221 Broadway Lofts',
    },
    'cobuembed.groups_heading': {
      label: 'Groups Tab Heading',
      type: 'text',
      defaultValue: 'Groups to join and create',
    },
    'cobuembed.chat_heading': {
      label: 'Chat Tab Heading',
      type: 'text',
      defaultValue: 'Chat with your neighbors',
    },
    'cobuembed.market_heading': {
      label: 'Market Tab Heading',
      type: 'text',
      defaultValue: 'Buy, sell, and share with neighbors',
    },
    'cobuembed.powered_by': {
      label: 'Powered By Label',
      type: 'text',
      defaultValue: 'Community Powered by',
    },
  },
  slotGroups: {
    'cobuembed.tour_cta': tourCtaDef,
    'cobuembed.cobu_link': cobuLinkDef,
    'cobuembed.event1': event1Def,
    'cobuembed.event2': event2Def,
    'cobuembed.group1': group1Def,
    'cobuembed.group2': group2Def,
    'cobuembed.group3': group3Def,
    'cobuembed.group4': group4Def,
    'cobuembed.chat1': chat1Def,
    'cobuembed.chat2': chat2Def,
    'cobuembed.market1': market1Def,
    'cobuembed.market2': market2Def,
    'cobuembed.market3': market3Def,
  },
};

(CobuEmbed as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default CobuEmbed;
